import { getUserToken } from "src/utils/index";
import { validateResponseData } from "../utils/ProcessResponseUtils";

class SuittService {
  constructor() {
    this._apiBase = process.env.REACT_APP_API;
  }

  async sendRequest(
    route,
    method = "GET",
    data = {},
    headers = {
      Accept: "application/json",
    },
    isBlob = false,
    signal
  ) {
    const payloud =
      method === "GET"
        ? {
            method,
            headers,
            signal,
          }
        : {
            method,
            body: JSON.stringify(data),
            headers,
            signal,
          };
    try {
      const res = await fetch(`${this._apiBase}${route}`, payloud);
      const parsedResponseData = isBlob ? await res.blob() : await res.json();
      return validateResponseData(parsedResponseData);
    } catch (error) {
      return {
        code: 404,
        message: "Errors",
        errors: [
          {
            errorCode: 409172,
            errorMessage: "Internal Server Error",
          },
        ],
      };
    }
  }

  async doLogin({ login = "", password = "" }) {
    const res = await this.sendRequest(
      "/auth/login",
      "POST",
      {
        login,
        password,
      },
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": "guest",
      }
    );
    return res;
  }

  async doRegister(payload = {}) {
    const res = await this.sendRequest("/auth/register", "POST", payload, {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-access-token": "guest",
    });
    return res;
  }

  async getUsers(
    {
      status = "",
      orgUnit = "",
      search = "",
      accountType = "",
      page = 1,
      limit = 10,
      sortBy = "lastActivity",
      sortOrder = "desc",
    } = {},
    signal
  ) {
    const params = new URLSearchParams({
      status,
      orgUnit,
      search,
      accountType,
      page: String(page),
      limit: String(limit),
      sortBy,
      sortOrder,
    });
    const res = await this.sendRequest(
      `/users?${params.toString()}`,
      "GET",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      },
      false,
      signal
    );
    return res;
  }

  async getOrgUnits() {
    const res = await this.sendRequest(
      `/org-units`,
      "GET",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": "guest",
      }
    );
    return res;
  }

  async getOrgUnitsTree() {
    const res = await this.sendRequest(
      `/org-units/tree`,
      "GET",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": "guest",
      }
    );
    return res;
  }

  async createOrgUnit({
    code = "",
    nameUk = "",
    type = "",
    parentCode = null,
  }) {
    const res = await this.sendRequest(
      `/org-units`,
      "POST",
      { code, nameUk, type, parentCode },
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      }
    );
    return res;
  }

  async updateOrgUnit(id = "", data = {}) {
    const res = await this.sendRequest(`/org-units/${id}`, "PUT", data, {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-access-token": getUserToken(),
    });
    return res;
  }

  async deleteOrgUnit(id = "") {
    const res = await this.sendRequest(
      `/org-units/${id}`,
      "DELETE",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      }
    );
    return res;
  }

  async getUserInfo(userId = "") {
    const res = await this.sendRequest(
      `/users/${userId}`,
      "GET",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      }
    );
    return res;
  }

  async updateUserInfo({ user = {} }) {
    const res = await this.sendRequest(`/users/${user._id}`, "PUT", user, {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-access-token": getUserToken(),
    });
    return res;
  }

  async sendSupportForm(data = {}) {
    const res = await this.sendRequest("/support", "POST", data, {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-access-token": "guest",
    });
    return res;
  }

  async getUserWorks(userId = {}) {
    const res = await this.sendRequest(
      `/users/${userId}/works`,
      "GET",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      }
    );
    return res;
  }

  async downloadUserDataXlsx(userId = {}) {
    const res = await this.sendRequest(
      `/users/${userId}/export/xlsx`,
      "GET",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      },
      true
    );
    return res;
  }

  async downloadUsersDataXlsx() {
    const res = await this.sendRequest(
      `/users/export/xlsx`,
      "GET",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      },
      true
    );
    return res;
  }

  async downloadUserWorksDataXlsx({ userId = "" }) {
    const res = await this.sendRequest(
      `/users/${userId}/works/export/xlsx`,
      "GET",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      },
      true
    );
    return res;
  }

  async changeUserStatus({ userId = "", status = "inactive" }) {
    const res = await this.sendRequest(
      `/users/${userId}/status`,
      "PUT",
      {
        userId,
        status,
      },
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      }
    );
    return res;
  }

  async removeUser(userId = "") {
    const res = await this.sendRequest(
      `/users/${userId}`,
      "DELETE",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      }
    );
    return res;
  }

  async addUserWork({ userId = "", data = {} }) {
    const res = await this.sendRequest(`/users/${userId}/works`, "POST", data, {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-access-token": getUserToken(),
    });
    return res;
  }

  async removeUserWork({ userId = "", workId = {} }) {
    const res = await this.sendRequest(
      `/users/${userId}/works/${workId}`,
      "DELETE",
      {},
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      }
    );
    return res;
  }

  async resetUserPassword(userId = "", password = "") {
    const res = await this.sendRequest(
      `/auth/password/reset`,
      "POST",
      {
        userId,
        password,
      },
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": getUserToken(),
      }
    );
    return res;
  }
}

export default SuittService;
